/**
 * Mood analytics (F3, simple statistic without AI): distribution and
 * evolution of mood over time.
 */
import { createRoute, useSearch } from "@tanstack/solid-router";
import { useNavigate } from "@tanstack/solid-router";
import { msg } from "@lingui/core/macro";
import { appRoute } from "./route";
import { PageContainer } from "@fleet/ui";
import { AppErrorBoundary } from "@/components/app-error-boundary";
import { useI18n } from "@/providers/i18n-provider";
import { MoodAnalytics, type MoodPeriod } from "@/features/journal/components/mood-analytics";

function MoodPage() {
  const { t } = useI18n();
  const navigate = useNavigate();
  const search = useSearch({ from: "/_app/mood" });

  // The series period (week/month) lives in the URL (?period=) so a link to
  // analytics preserves the chosen view and is idempotent on reload.
  const period = (): MoodPeriod => (search().period === "week" ? "week" : "month");
  const setPeriod = (next: MoodPeriod) => {
    void navigate({
      to: "/mood",
      // Omit the default (month) to keep the URL clean.
      search: next === "month" ? {} : { period: next },
      replace: true,
    });
  };

  return (
    <PageContainer maxWidth="2xl" class="flex flex-col gap-5 pb-16">
      <h1 class="pt-2 text-xl font-bold text-foreground">{t(msg`Mood insights`)}</h1>
      <AppErrorBoundary label="mood">
        <MoodAnalytics period={period} onPeriodChange={setPeriod} />
      </AppErrorBoundary>
    </PageContainer>
  );
}

interface MoodSearch {
  period?: MoodPeriod;
}

export const moodRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/mood",
  validateSearch: (raw: Record<string, unknown>): MoodSearch => ({
    period: raw.period === "week" ? "week" : undefined,
  }),
  component: MoodPage,
});
